/* @flow */
import pb from './pubsub';
const utils = require('./utils');
import Tooltip from './tooltip';
require('./search'); // needs to load after tooltip because it imports help to label dynamically generated controls
require('./preview');
require('./onboard');
import { stateFactoids } from './help'; // needs to load after tooltip to attach tooltips to controls
import type { GeoJSONFeature, GeoJSONGeometryCollection } from '@mapbox/geojson-types';

import { NS, Marker, ranks, mapDimensions, LatLng, City, GeoCodeGoodResult, categories, categoricalGradient, ZipsByRank, THEME } from './constants';
import { MapStorage } from './storage';
import CityMap from './citymap';
import markers from './markers';
import { formatRanks, requestInfo, requestRanks, requestRanksMultiYear, requestDeltaRanksMultiYear } from './storage/predict';
import { capitalize } from '../server/utils';
import { Controls } from './controls'
import { type granuleSize } from '../common/types'

const pubSub = pb.setup('index')
const granularity = utils.granularity

/*::
import CSS from 'nativeObjects'
import SVGElement from 'nativeObjects'
import d3 from 'javascriptLibs'
import topojson from 'javascriptLibs'
import Sentry from 'javascriptLibs'
 */

interface TopoJsonForZips {
    type: string;
    objects: {
        zips?: GeoJSONGeometryCollection,
        tracts?: GeoJSONGeometryCollection
    };
    arcs: [number, number][][];
}
type RankedZipcodes = string[][];

// log all entries to Sentry
if (window.prefetched.sentryKey && window.Sentry) {
    // Sentry may be blocked by ad-blockers
    Sentry.init({ dsn: window.prefetched.sentryKey });
}

const _getCity = (r) => r.csa ? { csa: r.csa } : { msa: r.msa }

let currentYear = new Date().getFullYear()
let zoomedInState = null;
let zoomedInCity = null;
let needToRecomputeUs = false;

// type region = `${STATE}_${granuleSize}`
let cachedStates: { [region]: {| ranks: Object, stateJson: Object |} } = {};

let searchParams = new URL(document.location.href).searchParams
let focusLocation = searchParams.get('focus')
let query = {}
for (let [k, v] of searchParams) {
    query[k] = v
}
let granule: granuleSize = searchParams.get('granularity') || 'county'
const regionalGranularity = {
    country: ['county', 'zip'],
    state: ['county', 'zip', 'tract'],
    city: ['county', 'zip', 'tract', 'h3']
}
// this function ensures the granularity is within allowed limits for this level
function fixGranularity() {
    if (zoomedInCity) {
        if (!regionalGranularity.city.includes(granule)) {
            pubSub.publish('notify:error', `Granularity of "${granule}" is not compatible with this region size, falling back to "${regionalGranularity.city[0]}"`)
            granule = regionalGranularity.city[0]
        }
    } else if (zoomedInState) {
        if (!regionalGranularity.state.includes(granule)) {
            pubSub.publish('notify:error', `Granularity of "${granule}" is not compatible with this region size, falling back to "${regionalGranularity.state[0]}"`)
            granule = regionalGranularity.state[0]
        }
    } else if (!regionalGranularity.country.includes(granule)) {
        pubSub.publish('notify:error', `Granularity of "${granule}" is not compatible with this region size, falling back to "${regionalGranularity.country[0]}"`)
        granule = regionalGranularity.country[0]
    }
    pubSub.publish('sync:store', { granule })
}
pubSub.subscribe('zoom:granularity', (val) => {
    console.log('received')
    granule = val
    if (zoomedInCity) {
        // updateRegionGranularity(granule)
        zoomToCity(zoomedInCity, zoomedInState, undefined, true, true)
    } else if (zoomedInState) {
        updateRegionGranularity()
    } else {
        console.log('us')
        updateUsGranularity()
    }
})

let mapStore = new MapStorage();
const tools = new Tooltip();
const cityMap = new CityMap();
let controls = new Controls(query)

let timelineYearLabel = ((document.getElementById('timeline-subtitle'): any): HTMLElement);
let timelineYear = ((document.getElementById('timeline-year'): any): HTMLInputElement);
let timelineClose = ((document.getElementById('close-timeline'): any): HTMLElement);
timelineYear.innerText = controls.selectedYear

let timelineStats = ((document.getElementById('timeline-stats'): any): HTMLInputElement);

var startTime = Date.now();
pubSub.publish('notify:startLoad', 'Calculating individual granules...');

let computeButton = ((document.getElementById('compute-stats-btn'): any): HTMLInputElement);
let warningsBox = ((document.getElementById('data-warnings'): any): HTMLElement);
computeButton.addEventListener('click', (e: MouseEvent) => {
    computeButton.disabled = true
    computeRanks(controls.selectedYear, controls.getSelectedStats());
    giveWarnings()
    pubSub.publish('tooltip:hide')
});

function giveWarnings() {
    if (selectedYear > 2023) {
        warningsBox.innerText = 'Future simulations may be inaccurate, use at your own risk.'
    } else if (selectedYear < 1995) {
        warningsBox.innerText = 'Simulations going too far into the past may be inaccurate, use at your own risk.'
    } else {
        warningsBox.innerText = ''
    }
}
giveWarnings();

let listOfStates;
let waitForListOfStates = utils.externalPromise()

// helper method for scaling projection to best fit within bounding box
const scaleProjection = (projection, boundingBox) => {
    let initialScale = projection.scale();
    let initialOffset = projection.translate();
}

let bufferCanvas = ((document.getElementById('canvas-buffer'): any): HTMLCanvasElement),
    bufferCtx = bufferCanvas.getContext('2d');
let canvas = ((document.getElementById('canvas'): any): HTMLCanvasElement),
    ctx = canvas.getContext('2d');

function initCanvas(canvas) {
    canvas.width = mapDimensions.width;
    canvas.height = mapDimensions.height;
}
initCanvas(bufferCanvas)
initCanvas(canvas)
addEventListener('resize', (event) => {
    initCanvas(bufferCanvas)
    initCanvas(canvas)
    globalMapScale = Math.min(mapDimensions.width/albersWidth, mapDimensions.height/albersHeight)
    offsetX = (globalMapScale - 1) * albersWidth/2
    offsetY = (globalMapScale - 1) * albersHeight/2
    projection = d3.geoAlbersUsa().scale(globalMapScale * defaultScale).translate([defaultOffset[0] + offsetX, defaultOffset[1] + offsetY])
    svgPath = d3.geoPath().projection(projection)
    canvasPath = d3.geoPath().projection(projection).context(ctx)
    bufferCanvasPath = d3.geoPath().projection(projection).context(bufferCtx)
    _updateUsGranularity()

    d3.select("#main-svg").attr("width", mapDimensions.width).attr("height", mapDimensions.height);

    // update states layer
    stateMap
        .selectAll("path")
        .data(j.features).join(
            enter => enter.append('path') /* not used, we only care about update */,
            update => update.attr("d", svgPath)
        )

    // update cities layer
    citySvgMap.selectAll("circle")
        .data(fetchedCities.map(city => {
            const latLngList = city.lat_lng.split(';');
            const latLng = {
                lat: latLngList[0],
                lng: latLngList[1]
            }
            return {
                ...city,
                latLng
            };
        }), (d) => d['lat_lng']).join(
            enter => enter.append('circle') /* not used, we only care about update */,
            update => update
                .attr("cx", (d) => projection([d.latLng.lng, d.latLng.lat])[0])
                .attr("cy", (d) => projection([d.latLng.lng, d.latLng.lat])[1])
                .attr("r", (d) => Math.max(globalMapScale, 1) * (Math.max(2 * (314 - d.rank), 293) / 293) ** 3)
        )
})

var defaultScale = d3.geoAlbersUsa().scale();
var defaultOffset = d3.geoAlbersUsa().translate();
var albersWidth = 960;
var albersHeight = 500;
var globalMapScale = Math.min(mapDimensions.width/albersWidth, mapDimensions.height/albersHeight);
var offsetX = (globalMapScale - 1) * albersWidth/2;
var offsetY = (globalMapScale - 1) * albersHeight/2;
var projection = d3.geoAlbersUsa().scale(globalMapScale * defaultScale).translate([defaultOffset[0] + offsetX, defaultOffset[1] + offsetY]);
var svgPath = d3.geoPath().projection(projection);
var canvasPath = d3.geoPath().projection(projection).context(ctx);
var bufferCanvasPath = d3.geoPath().projection(projection).context(bufferCtx);

// helper function for generating url from city
const flattenCity = (c) => `${c.city},${c.state} ${c.latLng.lat},${c.latLng.lng}`

// helper function for updating url
function updateUrlOnZoomOrRanks() {
    let fields = controls.getSelectedStats()
    if (zoomedInCity) {
        fields['focus'] = flattenCity(zoomedInCity)
    } else if (zoomedInState) {
        fields['focus'] = zoomedInState;
    } else {
        delete fields['focus']
    }
    fields['granularity'] = granule
    let query = Object.keys(fields).map((k) => {
        return `${k}=${fields[k]}`
    }).join('&');
    if (query) {
        query = '?' + query;
    }
    if (controls.selectedYear !== currentYear) {
        query += `&year=${controls.selectedYear}`
    }
    return `${location.origin}${location.pathname}${query}`;
}

var svg = d3.select("#map-container")
    .append("svg")
      .attr('id', 'main-svg')
      .attr("width", mapDimensions.width)
      .attr("height", mapDimensions.height);
      svg.append("g")
      
const stateGranules = svg.append('g').attr("id", "zipcodes_highres")
const stateMap = svg.append('g').attr("id", "states")
const citySvgMap = svg.append('g').attr("id", "cities")
let citySvgStateMap = svg.append('g').attr('id', "cities_highres")

let j
d3.json("/static/json/state_map.json").then(json => {
    j = json
    stateMap
        .attr("class", "states")
        .on("mouseout", unhoverUsMapView)
        .selectAll("path")
        .data(json.features)
        .enter().append("path")
        .attr("class", "us_state")
        .attr("id", (d) => 'state_' + d.properties.label)
        .attr("data-name", (d) => d.properties.name)
        .attr("data-label", (d) => d.properties.label)
        .on("mouseover", hoverOverElementInUsMapView)
        .on("click", zoomToStateByClick)
        .attr("d", svgPath);
    listOfStates = [...document.getElementsByClassName("us_state")];
    waitForListOfStates.resolve()

    if (focusLocation) {
        // hide states if focusing on more specific area
        document.getElementById('states').style.display = "none";
    }
});

window.pubSub = pubSub;

// preload zipcode map to save time (NOT USED)
function drawImageScaled(img, ctx) {
    var canvas = ctx.canvas;
    var hRatio = canvas.width / img.width;
    var vRatio =  canvas.height / img.height;
    var originalAlbersRatio = Math.min(img.width / albersWidth, img.height / albersHeight);
    var ratio  = Math.min(hRatio, vRatio);
    // var ratio = hRatio;
    var centerShiftX = (canvas.width - img.width * ratio) / 2;
    var centerShiftY = (canvas.height - img.height * ratio) / 2;  
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, centerShiftX, centerShiftY, img.width * ratio, img.height * ratio);  
    // ctx.drawImage(img, 0,0, img.width, img.height, centerShiftX, centerShiftY, img.width * ratio, img.height * ratio);  
}
// var mapImage = new Image();
// mapImage.onload = () => {
//     drawImageScaled(mapImage, ctx);
//     // ctx.drawImage(mapImage, 0, 0);
// };
// mapImage.src = '/static/cached-map.png';

let zipMap = svg.append("g").attr('id', 'zip-map'); // we seem to transform this for state view, but I don't see it being used
let needToDrawUs = false

/*
 * Initial load for US level map + path rendering based on granularity
 */
let worker = new Worker('./zip-worker.js') // worker is used to compress map data stored in localCache (to fit within 5MB limit)
let workerCallback = null
worker.addEventListener('message', (msg: MessageEvent<any>) => {
    switch (msg.data.cmd) {
        case 'zip':
            localStorage.setItem(`${granule}-map-paths`, msg.data.val)
            break
        case 'unzip':
            granularity[granule].usMapPaths = msg.data.val
            break
    }
    if (workerCallback) {
        workerCallback()
        workerCallback = null
    }
});
// let worker = new Worker(new URL('./zip.worker.js', import.meta.url))

let firstLoad = true
function _updateUsGranularity() {
    // map pre-fetched ranks so we can apply them in linear time
    // let zipToRank = {};
    // if (window.prefetched.ranks) { // TODO: only on first load
    //     Object.keys(window.prefetched.ranks.data).forEach(rank => {
    //         const group = window.prefetched.ranks.data[rank];
    //         group.forEach(z => zipToRank[z] = parseInt(rank) - 1);
    //     })
    // }

    // if (!focusLocation) {
    //     // load map of US
    //     history.pushState({
    //         region: null,
    //         year: controls.selectedYear,
    //         stats: controls.getSelectedStats(),
    //         granule
    //     }, 'USA map', updateUrlOnZoomOrRanks());
    // } else {
    //     // TODO: would we ever call _updateUsGranularity w/ focusLocation set?
    //     // load zoomed in area
    //     needToDrawUs = true
    //     needToRecomputeUs = !!selectedStats
    // }

    history.pushState({
        region: null,
        year: controls.selectedYear,
        stats: controls.getSelectedStats(),
        granule,
    }, 'USA map', updateUrlOnZoomOrRanks());

    generateBuffer()

    needToDrawUs = true
    needToRecomputeUs = Object.keys(selectedStats).length !== 0
    console.log("_updateUsGranularity", needToRecomputeUs)
    if (needToRecomputeUs && !focusLocation) {
        pubSub.publish('notify:startLoad', 'Recalculating Country...');
        requestRanks(selectedYear, selectedStats, void 0, granule).then(applyUsRanks).catch(error => {
            pubSub.publish('notify:stopLoad');
            pubSub.publish('notify:error', error);
        });
    }

    if (firstLoad) {
        if (!focusLocation) {
            // NOTE: rendering separate paths for elements with small bounding boxes is actually faster than as a single path
            if (window.prefetched.ranks) {
                applyUsRanks(formatRanks(window.prefetched.ranks))
            } else {
                applyUsRanks()
            }
        } else {
            let cityRegex = /([A-Za-z .-]+)\s*,\s*([A-Za-z]{2})\s(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/
            if (/^[A-Za-z]{2}$/.test(focusLocation)) {
                // i.e. focus=MA
                zoomToStateByName(focusLocation, true)
            } else if (cityRegex.test(focusLocation)) {
                // i.e. focus=San Diego,CA 45.212,42.3123
                let loc = focusLocation.match(cityRegex)
                zoomToCity({
                    city: loc[1],
                    state: loc[2],
                    latLng: {
                        lat: loc[3],
                        lng: loc[4]
                    }
                }, loc[2])
            }
        }

        const regionData = {
            showCities: citiesVisible,
            granule,
            region: {}
        }
        if (zoomedInCity) {
            regionData.region = zoomedInCity
        } else if (zoomedInState) {
            regionData.region = { state: zoomedInState }
        }
        // populate region dashboard
        pubSub.publish('setup:region', regionData);
    }
    
    // svg.append(zipMap);
    // @flow-ignore: TODO: this is temporary
    // document.getElementById("render-time").innerHTML = `${Date.now() - startTime} (z)`
    if (window.prefetched.error) {
        pubSub.publish('notify:error', window.prefetched.error);
    }
    // pubSub.publish('ranks:appliedCountry');
    pubSub.publish('notify:stopLoad');
    // fetchCities();
}

// update US-level granularity
function updateUsGranularity() {
    return new Promise((resolve, reject) => {
        fixGranularity()
        const zipCodes = zipMap
            // .each(function () {
            //     // inserts new .zipcodes group 
            //     this.parentNode.insertBefore(this, this.parentNode.firstChild);
            // })
            // .attr("class", "zipcodes")
        const paths = localStorage.getItem(`${granule}-map-paths`)
        workerCallback = () => {
            _updateUsGranularity()
            resolve()
        }
        if (paths) {
            // paths already cached
            worker.postMessage({
                cmd: 'unzip',
                val: paths
            })
        } else {
            // first fetch
            d3.json(`/static/json/${granule}_map.json`).then((us: TopoJsonForZips) => {
                granularity[granule].usMapPaths = {}
                zipCodes.selectAll("path")
                .data(topojson.feature(us, us.objects[granularity[granule].plural]).features)
                .enter().each((d) => {
                    let id = granularity[granule].usId(d)
                    // console.log('zd', zip, d)
                    granularity[granule].usMapPaths[id] = d
                })
                worker.postMessage({
                    cmd: 'zip',
                    val: granularity[granule].usMapPaths
                })
            })
        }
    })
}
waitForListOfStates.then(() => {
    listOfStates.forEach(e => {
        // @flow-ignore: valid SVG property
        e.style.stroke = "none";
        // @flow-ignore: valid SVG property
        e.style.fill = "white";
        e.style.opacity = '0.01';
    })
})
updateUsGranularity()
fetchCities();

function updateRegionGranularity(granuleSize: string) {
    zoomToStateByName(zoomedInState)
}


// show legend
const legendTrigger = ((document.getElementById('legend-hook'): any): HTMLElement);
const legend = ((document.getElementById('legend'): any): HTMLElement);
const desc = ((document.getElementById('legend-description'): any): HTMLElement);
const legendRanks = ((document.getElementById('legend-ranks'): any): HTMLElement);
type color = string;
type category = string;
ranks.forEach((rank, index) => {
    const node = document.createElement("DIV");
    const color = document.createElement("DIV");
    color.classList.add('legend-color-box');
    color.style.background = rank;
    node.id = `legend-rank-${index}`;
    node.classList.add('legend-box');
    node.appendChild(color);
    legendRanks.appendChild(node);
});
/*if (!localStorage.getItem('onboarded')) {
    legend.style.display = "block";
    localStorage.setItem('onboarded', 'yes');
}*/

let tooltip = ((document.getElementById('state-tooltip'): any): HTMLElement);
let tooltipZoomed = ((document.getElementById('zoom-tooltip'): any): HTMLElement);
let tooltipTitle = ((document.getElementById('state-tooltip-title'): any): HTMLElement);
let tooltipInfo = ((document.getElementById('state-tooltip-info'): any): HTMLElement);
const tooltipClose = ((document.getElementById('state-tooltip-close'): any): HTMLElement);

function getBounds(svg: SVGElement) {
    let box = svg.getBBox();
    return [
        [box.x, box.y],
        [box.x + box.width, box.y + box.height]
    ];
}
// helper function to give user immediate feedback without hammering servers for info
// maybe move this to tooltip module, we seem to have different implementations of show tooltip logic
export function showTooltip(d:Object, data:Object, alignToFeature:boolean=true) {
    // populate tooltip
    tooltipZoomed.style.display="none";
    cityMap.resetTooltip();
    tooltipTitle.innerHTML = data.title;
    delete data.title;
    tooltipInfo.innerHTML = 'No information'
    if (data.loading) {
        tooltipInfo.innerHTML = 'Loading...'
    } else if ('text' in data) {
        // show a preview of content
        let max_preview = 30
        if (data.text) {
            tooltipInfo.innerHTML = data.text.length > max_preview ? data.text.slice(0, max_preview) + '...' : data.text
        } else {
            // no description entered, just title
            tooltipInfo.innerHTML = ''
        }
    } else if (Object.keys(data).length) {
        if (data.needLogin) {
            tooltipInfo.innerHTML = ' Login to see more info'
        } else {
            // separate line for each stat
            delete data.needLogin;
            tooltipInfo.innerHTML = Object.keys(data).filter(k => k[0] !== '_').map(k => k + ': ' + data[k]).join('<br/>');
        }
    } else if (!Object.keys(selectedStats).length) {
        // no stats selected, user just wants to know the name of zip
        tooltipInfo.innerHTML = ''
    }
    tooltip.style.display = "block";

    // align it
    let bounds = (d instanceof SVGElement) ? getBounds(d) : svgPath.bounds(d);
    // let containerBounds = canvas.getBoundingClientRect();
    let tooltipBounds = tooltip.getBoundingClientRect();
    tooltip.style.width = "";
    if (alignToFeature) {
        tooltip.classList.remove('static')
        let offsetLeft = (bounds[0][0] + bounds[1][0])/2 * scale + translate[0],
            offsetTop = bounds[1][1] * scale + translate[1],
            buffer = bounds[1][1] - bounds[0][1] < 40 ? 20 : 5; // give larger margin to smaller elements to avoid tooltip being blocked by mouse
        // tooltip.style.left = offsetLeft + containerBounds.left - tooltipBounds.width/2 + "px";
        // tooltip.style.top = offsetTop + containerBounds.top + buffer + "px";
        tooltip.style.left = offsetLeft - tooltipBounds.width/2 + "px";
        tooltip.style.right = "";
        if (offsetTop < (canvas.height / 2)) {
            // top half of the screen, tooltip renders down
            tooltip.style.top = offsetTop + buffer + "px";
            tooltip.style.bottom = "";
        } else {
            // bottom half, render upwards
            offsetTop = bounds[0][1] * scale + translate[1]
            tooltip.style.bottom = (canvas.height - offsetTop + 5) + "px";
            tooltip.style.top = "";
        }
    } else {
        tooltip.classList.add('static')
        tooltip.style.left = "";
        tooltip.style.bottom = "";
        tooltip.style.top = "10px";
        tooltip.style.right = "10px";
    }
}
window.showTooltip = showTooltip

// fetch/render infographic tooltip for zipcode, uses cache when possible and debounces server requests to only request data when user
// pauses movement
let tooltipCache = {}
let _showTooltip = false;   // safety to avoid re-opening closed tooltip after server response

let _fetchAndShowTooltip = utils.debounce((d, key, alignToFeature) => requestInfo(selectedYear, selectedDelta, selectedStats, {
    type: granule,
    id: granularity[granule].stateId(d)
}).then(data => {
    data.title = granularity[granule].name(d, data._name)
    tooltipCache[key] = data
    if (_showTooltip) {
        showTooltip(d, tools.curateData(data, controls), alignToFeature)
    }
}))
function fetchAndShowTooltip(d, alignToFeature = true) {
    let key = `${granularity[granule].stateId(d)} ${selectedYear} ${JSON.stringify(selectedStats)}`
    _showTooltip = true;
    if (tooltipCache[key])  {
        showTooltip(d, tools.curateData(tooltipCache[key], controls), alignToFeature)
    } else {
        _fetchAndShowTooltip(d, key, alignToFeature)
    }
}
window.cache = tooltipCache
pubSub.subscribe('tooltip:zip', d => fetchAndShowTooltip(d, false))

export function hideTooltip() {
    _showTooltip = false;
    tooltip ? tooltip.style.display = "none" : null;
}
window.hideTooltip = hideTooltip

tooltipClose.addEventListener('click', (e: MouseEvent) => {
    hideTooltip()
    pubSub.publish('location:cancel')
})

function hoverOverElementInUsMapView(d) {
    let element = d3.select(this).node();
    listOfStates.forEach(element => {
        element.style.opacity = '0.7';
        // @flow-ignore: flow doesn't know SVG
        element.style.strokeWidth = 2;
        // @flow-ignore: flow doesn't know SVG
        element.style.stroke = "black";
    })
    element.style.opacity = '0.01';
    // showTooltip(d, { title: (element: any).__data__.properties.name });
    showTooltip(d, { title: (element: any).__data__.properties.name, ...stateFactoids[(element: any).__data__.properties.label] });
}

function unhoverUsMapView(d) {
    listOfStates.forEach(element => {
        element.style.opacity = '0.01';
    });
    hideTooltip();
}

// helper function used by search:zipcode and search:marker
async function zoomToZipcode(zip, cb, publishEvent = true) {
    pubSub.publish('zoom:cleanup');
    // if we're using wrong granularity, switch to zip
    if (granule !== 'zip') {
        granule = 'zip'
        await updateUsGranularity()
    }
    const data = granularity[granule].usMapPaths[zip]
    cityMap.loadRegion(data.geometry.coordinates, null, true);

    //let state = data.properties.s;
    //let stateElement = d3.select('#state_' + state);
    // let stateElement = ((document.getElementById('state_' + state): any): HTMLElement);
    // TODO: consider replacing this with fetchRelatedRegion, problem is zipcode is not guaranteed
    // to be part of CSA/MSA
    loadStateSilently(data.properties.s, () => {
        cityMap.loadRegion(data.geometry.coordinates);
        cityMap.loadZipcodeMap(d3.select(`#${CSS.escape(zip)}_${granule}_state`).data()[0], cb, publishEvent);
        pubSub.publish('notify:stopLoad');
    });
}

// helper function used to zoom in to city directly
// NOTE: this city object is not technically a City we store in constants, it's an object
// that needs to be compatible with json's version of city representation as well
function zoomToCity(city: City, state: string, cb: Function, publishEvent = true, granularityOnly = false) {
    if (state !== zoomedInState) {
        citiesFetched = false // if we're changing states, we need to refetch cities for new state later
    }
    hideTooltip()
    if (granularityOnly) {
        pubSub.publish('notify:startLoad', 'Adjusting Precision...');
    } else {
        pubSub.publish('zoom:cleanup');
        pubSub.publish('notify:startLoad', 'Zooming In...');
        cityMap.loadLocation(city.latLng, cb);
    }

    // let stateElement = d3.select('#state_' + state);
    // loadStateSilently(state, () => {
    //     pubSub.publish('notify:load:stop');
    //     if (publishEvent) {
    //         pubSub.publish('location:load', {
    //             type: 'city',
    //             query: `${city.city}, ${state}`
    //         });
    //     }
    // });
    fetchRelatedRegion('city', city).then(([cityJson, cityRanks]) => {
        pubSub.publish('notify:stopLoad');
        if (publishEvent && !granularityOnly) {
            pubSub.publish('location:load', {
                type: 'city',
                query: `${city.city}, ${state}`
            });
        }
        stateGranules
            .attr("class", zoomedInState)
            .attr("display", "none")
          .selectAll("path")
            .data(cityJson.features)
            .enter().append(createGranuleElement(true)).exit().remove();
        zipMap.node().style.display = "none";
        pubSub.publish('zoom:enhance', {
            geo: cityJson
        })
        if (cityRanks) {
            // NOTE: this should be called AFTER zoom:enhance to avoid citymap running geoJsonLayer.setStyle
            // on stale svg data from zipcodes_highres being removed by the logic above
            applyRegionRanks(cityRanks);
        }
    })
}
// triggered by search for city
pubSub.subscribe(['mainSearch:city', 'zoom:city'], (c: City) => {
    zoomToCity(c, c.state, undefined, false);
})

// helper function to zoom to marker directly
export function zoomToMarker(marker: Marker) {
    pubSub.publish('zoom:cleanup');
    pubSub.publish('notify:startLoad', 'Zooming In...');
    cityMap.loadLocation(marker.latLng, () => {
        cityMap.selectMarker(marker);
    });

    // @flow-ignore: data-state is user-defined property
    const zipData = granularity[granule].usMapPaths[marker.zip]
    let stateName = zipData.properties.s;
    let stateElement = d3.select('#state_' + stateName);
    loadStateSilently(granularity[granule].usMapPaths[marker.zip].properties.s, () => {
        pubSub.publish('notify:stopLoad');
    });
}
window.zoomToMarker = zoomToMarker
pubSub.subscribe('marker:open', zoomToMarker)
function previewMarker(marker: Marker) {
    pubSub.publish('zoom:cleanup');
    cityMap.loadLocation(marker.latLng, () => {})
}
pubSub.subscribe('marker:preview', previewMarker)

// zoom in in response to search
pubSub.subscribe('mainSearch:zipcode', (zip) => zoomToZipcode(zip, undefined, false));

// zoom in to a marker, create if it doesn't exist
pubSub.subscribe('mainSearch:marker', (marker) => {
    loadStateSilently(granularity[granule].usMapPaths[marker.zip].properties.s, () => {
        cityMap.loadLocation(marker.latLng, () => {
            cityMap.selectMarker(marker);
        });
    });
    // TODO: select marker on zoom
    // zoomToZipcode(marker.zip, () => {
    //     pubSub.publish('zoom:marker', { latlng: { lat: marker.lat, lng: marker.lng } });
    // });
});

// zoom in to address
pubSub.subscribe('mainSearch:geocode', (loc: GeoCodeGoodResult) => {
    loadStateSilently(loc.state, () => {
        if (loc.zip) {
            // we're zooming to city level
            cityMap.loadLocation(loc.latLng, () => {
                if (['address', 'point', 'street'].includes(loc.quality)) {
                    cityMap.createOrSelectMarker(loc.latLng, loc.zip, loc.street);
                }
                pubSub.publish('notify:stopLoad');
            }, 17);
        } else {
            // we're zooming to state level
            stateGranules.selectAll('path').some(d => {
                if (d3.geoContains(d, [loc.latLng.lng, loc.latLng.lat])) {
                    zoomToZipcode(granularity[granule].stateId(d), undefined, false);
                    return true
                }
            });
        }
    });
})
 
// silent load is typically used to load past state (into zip/city) while still fetching all the data for state
function loadStateSilently(state: string, callback: Function) {
    stateGranules.style('visibility', false);
    let s = Date.now();
    const region = `${state}_${granule}`
    if (zoomedInState === state) {
        // state already loaded, just switching view
        callback();
    } else if (cachedStates[region]) {
        // different state loaded, but this state is cached
        zoomedInState = state;
        let { ranks, stateJson } = cachedStates[region]
        improveStateResolution(ranks, stateJson, true);
        callback();
    } else {
        // state not cached, load from server
        zoomedInState = state
        zoomedInRegion = { state: zoomedInState }
        console.log("laod state silently", zoomedInState)
        Promise.all([
            requestRanks(selectedYear, selectedStats, zoomedInRegion, granule),     // state ranks
            d3.json(`/static/json/${granule}/${state}.json`),                       // state topojson
        ]).then((args) => {
            let [ranks, stateJson] = args
            cachedStates[region] = { ranks, stateJson }
            improveStateResolution(ranks, stateJson, true);
            callback();
        });
    }
}

// helper function that caches json after first fetch
// The Promise.all clause here is very similar to loadStateSilently, in both cases we're updating cachedStates,
// the only difference is which value we return from the cache. Consider refactoring the logic calling
// this and loadStateSilently function to use the same flow.
function _fetchStateJson(state: string) {
    const region = `${state}_${granule}`
    if (cachedStates[region]) {
        return Promise.resolve(cachedStates[region].stateJson)
    } else {
        // state not cached, load from server
        // return Promise.all([
        //     requestRanks(selectedYear, selectedStats, { state }, granule),          // state ranks
        //     d3.json(`/static/json/${granule}/${state}.json`),                       // state geojson
        // ]).then((args) => {
        //     let [ranks, stateJson] = args
        //     cachedStates[region] = { ranks, stateJson }
        //     return stateJson
        // });
        return d3.json(`/static/json/${granule}/${state}.json`).then((stateJson) => {
            if (cachedStates[region]) {
                cachedStates[region].stateJson = stateJson
            } else {
                cachedStates[region] = { ranks: void 0, stateJson }
            }
            return stateJson
        });
    }
}

interface Region {
    csa?: string;
    msa?: string;
    zip?: string | number;
    city?: string;
    state?: string;
}

// takes a list of related granule IDs (filterList) and a list of svg objects corresponding to the shapes of all granules
// in a given state and filters it down just to granules in filterList in linear time
function filterRelatedGranules(filterList: [], granuleList: []) {
    const filterHash = {}
    filterList.forEach(val => {
        val = granularity[granule].normalizeId ? granularity[granule].normalizeId(val) : val
        filterHash[val] = true
    })
    window.filterHash = filterHash
    console.log('FHASh', filterHash, granuleList)
    if (granule === 'tract') {
        return granuleList.filter(g => {
            const tractId = granularity[granule].stateId(g)
            const tractBase = parseInt(tractId.slice(0, -2))
            return filterHash[tractBase]

            // if (filterHash[tractId]) {
            //     return true
            // } else {
            //     // let's try a fuzzy match using older tracts
            //     const tractBase = Math.floor(tractId / 100) * 100
            //     let tractSuffix = tractId % 100
            //     while (tractSuffix) { // non-zero tractSuffix means there was a split earlier
            //         tractSuffix--
            //         if (filterHash[tractBase + tractSuffix]) return true
            //     }
            //     return false
            // }
        })
    } else {
        return granuleList.filter(g => filterHash[granularity[granule].stateId(g)])
    }
    // return granuleList
}

// fetches related region, using cache when appropriate
let cachedRegion = null
function fetchRelatedRegion(relationship: 'city' | 'state' | 'county', item: Region) {
    _cachedDelta = null
    if (relationship === 'city') {
        // get related city
        let loc = item.zip ? item.zip : `${item.city},${item.state}`
        let stateList = []
        let ranks

        return fetch(`/api/predict/context/${granule}/of/city/${loc}`).then(r => r.json()).then(json => {
            console.log('related region', json, loc)
            // CSA will be missing if city is not part of a greater CSA (i.e. San Diego)
            const region = { [json.region.type]: json.region.id, children: json.children } // convert to old format for now (TODO: clean up later to use json directly)
            zoomedInRegion = _getCity(region)
            ranks = requestRanks(selectedYear, selectedStats, zoomedInRegion, granule)
            cachedRegion = region
            stateList = Object.keys(json.children)
            return Promise.all(stateList.map(s => _fetchStateJson(s)))
        }).then(stateJsonFiles => {
            console.log("GRASJF", granule, stateJsonFiles)
            zoomedInState = item.state;
            zoomedInCity = item
            history.pushState({
                region: { city: flattenCity(zoomedInCity) },
                year: controls.selectedYear,
                stats: controls.getSelectedStats(),
                granule
            }, `${zoomedInCity.city}, ${zoomedInCity.state} map`, updateUrlOnZoomOrRanks());
            let cityZipcodes = { type: "FeatureCollection", features: [] }
            let index = 0
            for (let stateJson of stateJsonFiles) {
                let features = topojson.feature(stateJson, stateJson.objects[granularity[granule].plural])
                console.log('sl', stateList[index], cachedRegion.children[stateList[index]])
                cityZipcodes.features = cityZipcodes.features.concat(
                    filterRelatedGranules(cachedRegion.children[stateList[index]], features.features)
                )
                // for (let zip of features.features) {
                //     if (granule !== 'zip' || cachedRegion.children[stateList[index]].includes(parseInt(zip.properties.ZCTA5CE10))) {
                //         cityZipcodes.features.push(zip)
                //     }
                // }
                index++
            }
            _updateStateGranules(true, cityZipcodes) // updates rank containers that we use for lookup at city level
            return Promise.all([cityZipcodes, ranks])
        })
    } else if (relationship === 'state') {
        // get related state
    } else {
        throw new Error('Not implemented')
    }
}

let scale = 1;
let translate = [0, 0];
let citiesFetched = false;
// triggered when user clicks state
function zoomToStateByClick(state: GeoJSONFeature) {
    // @flow-ignore: label and name properties will always exist
    let { label } = state.properties;
    pubSub.publish('location:load', {
        type: 'state',
        query: label
    });
    zoomToStateByName(label);
}
// triggered when user searches for state in search input and also via user clicks in UI
function zoomToStateByName(name: string, prefetched: boolean = false) {
    pubSub.publish('notify:startLoad', 'Recalculating Region...');

    let element = document.getElementById(`state_${name}`)
    let stateChanged = name !== zoomedInState
    if (element) {
        zoomedInState = name
        zoomedInRegion = { state: zoomedInState }
        fixGranularity()
        history.pushState({
            region: zoomedInRegion,
            year: controls.selectedYear,
            stats: controls.getSelectedStats(),
            granule,
        }, `${zoomedInState} state map`, updateUrlOnZoomOrRanks());
        canvas.style.display = "none";
        
        // @flow-ignore: SVGElement has getBBox
        var bounds = element.getBBox(),
            dx = bounds.width,
            dy = bounds.height,
            x = (bounds.x + bounds.x + bounds.width) / 2,
            y = (bounds.y + bounds.y + bounds.height) / 2;
        scale = 0.95 / Math.max(dx / mapDimensions.width, dy / mapDimensions.height);
        translate = [mapDimensions.width / 2 - scale * x, mapDimensions.height / 2 - scale * y];

        window.scale = scale
        window.translate = translate
        window.zipMap = zipMap

        zipMap.transition()
        .style("stroke-width", 0.5 / scale + "px")
        .attr("transform", `translate(${translate.join(',')}) scale(${scale})`)
        .on('end', async () => {
            document.getElementsByClassName('states')[0].style.display = "none";
            const region = `${name}_${granule}`
            if (cachedStates[region]) {
                let { ranks, stateJson } = cachedStates[region]
                console.log("cached region", region, ranks, stateJson)
                if (!ranks) {
                    // ranks weren't yet cached or have been invalidated
                    ranks = await requestRanks(selectedYear, selectedStats, { state: zoomedInState }, granule)
                }
                improveStateResolution(ranks, stateJson, false)
            } else {
                console.log("fetched region", region)
                Promise.all([
                    window.prefetched.ranks ? formatRanks(window.prefetched.ranks) : requestRanks(selectedYear, selectedStats, { state: zoomedInState }, granule),
                    d3.json(`/static/json/${granule}/${name}.json`),
                ]).then((args) => {
                    let [ranks, stateJson] = args
                    console.log("RANKS got", ranks)
                    improveStateResolution(ranks, stateJson, false)
                    console.log("RANKS again", ranks)
                    cachedStates[region] = { ranks, stateJson }
                }).catch(error => {
                    pubSub.publish('notify:stopLoad');
                    pubSub.publish('notify:error', error);
                });
            }
        });
        if (citySvgMap) {
            // may not yet exist if we're still loading the map with focus on state
            citySvgMap.attr("display", "none");
            markers.hide();
        }
        if ((!citiesFetched || stateChanged) && citiesVisible) {
            // render a state version of cities overlay
            citiesFetched = true
            let cityBorder = 3 / scale
            waitForFetchedCities.then(() => {
                citySvgStateMap
                    .attr("transform", `translate(${translate.join(',')}) scale(${scale})`)
                let groups = citySvgStateMap.selectAll("g").remove()
                    .data(fetchedCities.filter(city => city.state === name).map(city => {
                        const latLngList = city.lat_lng.split(';');
                        const latLng = {
                            lat: latLngList[0],
                            lng: latLngList[1]
                        }
                        return {
                            ...city,
                            latLng
                        };
                    })).enter().append('g')
                    groups.attr("stroke", "#333")
                        .on("mouseover", function (d) {
                            console.log(scale)
                            this.style.stroke = THEME.selected;
                            this.style.animation = `dash ${60 * scale}s linear infinite, glow 4s linear infinite`
                            //this.style.strokeWidth = cityBorder;
                            showTooltip(this,  { ...tools.curateData({
                                title: d.city,
                                urban_population: d.population_2018,
                                metro_population: d.msa ? d.msa.population_2018 : undefined,
                                region_population: d.csa ? d.csa.population_2017 : undefined,
                                urban_growth: d.growth,
                                metro_growth: d.msa ? d.msa.growth : undefined,
                                region_growth: d.csa ? d.csa.growth : undefined,
                            }), ...{ Trend: computeTrend(d) }});
                        })
                        .on("mouseout", function () {
                            hideTooltip();
                            this.style.stroke = "#333";
                            this.style.animation = ''
                            //this.style.strokeWidth = cityBorder;
                        })
                        .on("click", (d) => zoomToCity(d, d.state))
                    // .exit().remove()

                groups.append("circle")
                    .attr("cx", (d) => projection([d.latLng.lng, d.latLng.lat])[0])
                    .attr("cy", (d) => projection([d.latLng.lng, d.latLng.lat])[1])
                    .attr("r", (d) => Math.max(globalMapScale, 1) * (Math.max(2 * (314 - d.rank), 293) / 293) ** 3)
                    .attr("fill", "none")
                    .attr("stroke-width", cityBorder)
                    .attr("stroke-dasharray", cityBorder)

                //let svgBoxHeight = document.getElementById('main-svg').getBBox().height
                groups.append("text")
                    .text((d) => d.city)
                    .attr("x", (d) => projection([d.latLng.lng, d.latLng.lat])[0])
                    .attr("y", (d) => projection([d.latLng.lng, d.latLng.lat])[1] + Math.max(globalMapScale, 1) * (Math.max(2 * (314 - d.rank), 293) / 293) ** 3 + 8 / scale)
                    .attr("font-size", 15 / scale)
                    .attr("text-anchor", "middle")
                    .attr("stroke-width", 0)
                    .attr("pointer-events", "fill")
                    .attr("padding", "5px")
                    .attr("font-weight", "bold")
                    .attr('alignment-baseline', 'middle')
            })
        }

        // TODO: use attrTween to change transform while animation, may need to redraw state to zoom it

        // TODO: come back to later, transition animation
        // let t = d3.timer(function (elapsed) {
        //     let e = d3.select(this)
        //     ctx.beginPath();
        //     ctx.lineWidth = 0.5;
        //     // canvasPath(e.data()[0]);
        //     ctx.strokeStyle = '#999';
        //     ctx.fillStyle = e.style.fill;
        //     ctx.fill();
        //     ctx.stroke();
        //     if (elapsed > 750) t.stop();
        // });
    }
}
pubSub.subscribe('mainSearch:state', (q) => {
    zoomToStateByName(q.key)
});
pubSub.subscribe('zoom:state', (q) => {
    zoomToStateByName(q)
    zoomedInCity = null
});
pubSub.subscribe('zoom:reset', () => {
    citySvgStateMap.attr('display', 'none');
})
pubSub.subscribe('zoom:cleanup', () => {
    citySvgStateMap.attr('display', null);
})

// compute overall population trend for a given city
function computeTrend(d) {
    let core = d, csa = d.csa ? d.csa : d, msa = d.msa ? d.msa : d;
    let classification = 'Unclassified';
    if (core.growth > 0.01 && core.growth * 1.2 > msa.growth) {
        classification = 'Urban Growth'
        if (core.growth > 0.15) {
            classification = 'Rapid ' + classification
        } else if (core.growth > 0.07) {
            classification = 'Strong ' + classification
        } else if (core.growth < 0.02) {
            classification = 'Slight ' + classification
        }
    } else if (msa.growth > 0.03) {
        classification = 'Suburban Sprawl';
        if (msa.growth < 0.02) {
            classification = 'Slight ' + classification
        } else if (msa.growth > 0.15 && core.growth * 1.2 < msa.growth) {
            classification = 'Rapid ' + classification
        } else if (msa.growth > 0.07 && core.growth * 1.2 < msa.growth) {
            classification = 'Strong ' + classification
        }
    } else if (core.growth < 0) {
        classification = 'Decay';
        if (core.growth < -0.04) {
            classification = 'Rapid ' + classification
        } else if (core.growth > -0.02) {
            classification = 'Slight ' + classification
        }
    } else if (core.growth >= -0.01 && core.growth < 0.01 && msa.growth >= -0.01 && msa.growth < 0.01) {
        classification = 'Stagnation';
    }

    return classification;
}

let citiesVisible = true;
let fetchedCities = [];
let waitForFetchedCities = utils.externalPromise()
const normalizePopulation = (d) => {
    return d.msa ? d.msa.population_2018 : d.population_2018;
}
function fetchCities() {
    d3.json("/static/json/cities.json").then(cities => {
        fetchedCities = cities.filter(city => {
            // some states have a lot of "clutter" cities, that are really just a part of a larger city
            if (city.state === 'CA'  && city.population_2018 < 300000) {
                return false;
            } else if (city.state === 'TX' && city.population_2018 < 250000) {
                return false;
            } else if (city.state === 'AZ' && city.population_2018 < 300000) {
                return false;
            } else if (city.state === 'CO' && city.population_2018 < 200000) {
                return false;
            } else if (city.state === 'FL' && city.population_2018 < 180000) {
                return false;
            } else if (city.state === 'MA' && city.population_2018 < 120000) {
                return false;
            } else if (city.state === 'NC' && city.population_2018 < 170000) {
                return false;
            } else if (city.state === 'WA' && city.population_2018 < 150000) {
                return false;
            } else if (city.state === 'OR' && city.population_2018 < 120000) {
                return false;
            } else if (city.state === 'IL' && city.population_2018 < 140000) {
                return false;
            } else if (city.state === 'NJ' && city.population_2018 < 104000) {
                return false;
            } else if (city.state === 'MI' && city.population_2018 < 135000) {
                return false;
            }
            return true
        })
        waitForFetchedCities.resolve()
        if (focusLocation) {
            citySvgMap.attr('display', 'none')
        }
        // .attr("r", (d) => (Math.max(2 * (314 - d.rank), 293) / 293) ** 3)
        // .attr("r", (d) => Math.sqrt(Math.max(d.population_2018, 1e5) / 1e5))
        // .attr("r", (d) => Math.max(globalMapScale, 1) * Math.sqrt(normalizePopulation(d))/500)

        const cityList = [];
        citySvgMap.selectAll("circle")
            .data(fetchedCities.map(city => {
                const latLngList = city.lat_lng.split(';');
                const latLng = {
                    lat: latLngList[0],
                    lng: latLngList[1]
                }
                cityList.push({
                    city: city.city,
                    latLng,
                    state: city.state,
                    pop: city.msa ? city.msa.population_2018 : city.population_2018
                })
                return {
                    ...city,
                    latLng
                };
            })).enter().append("circle")
                .attr("cx", (d) => projection([d.latLng.lng, d.latLng.lat])[0])
                .attr("cy", (d) => projection([d.latLng.lng, d.latLng.lat])[1])
                .attr("r", (d) => Math.max(globalMapScale, 1) * (Math.max(2 * (314 - d.rank), 293) / 293) ** 3)
                .attr("fill", "rgba(255, 255, 255, 0.3)")
                .attr("stroke", "black")
                .on("mouseover", function (d) {
                    this.style.stroke = THEME.selected;
                    this.style.strokeWidth = 2;
                    showTooltip(this,  { ...tools.curateData({
                        title: d.city,
                        urban_population: d.population_2018,
                        metro_population: d.msa ? d.msa.population_2018 : undefined,
                        region_population: d.csa ? d.csa.population_2017 : undefined,
                        urban_growth: d.growth,
                        metro_growth: d.msa ? d.msa.growth : undefined,
                        region_growth: d.csa ? d.csa.growth : undefined,
                    }), ...{ Trend: computeTrend(d) }});
                })
                .on("mouseout", function () {
                    hideTooltip();
                    this.style.stroke = "black";
                    this.style.strokeWidth = 1;
                })
                .on("click", (d) => zoomToCity(d, d.state))
            .exit().remove();

        pubSub.publish('load:cities', cityList);
        markers.loadMarkersD3(projection);
        if (focusLocation) {
            markers.hide(); // otherwise this happens during the zoom in itself
        }
    });
}

// enable/disable cities layer
pubSub.subscribe('layer:cities', (show: bool) => {
    citiesVisible = show
    console.log('FFFU', show, zoomedInState)
    if (show) {
        if (zoomedInState) {
            citySvgStateMap.attr("display", "")
        } else {
            citySvgMap.attr("display", "")
        }
    } else {
        if (zoomedInState) {
            citySvgStateMap.attr("display", "none")
        } else {
            citySvgMap.attr("display", "none")
        }
    }
})

// export ability to edit events
export function editEvent(id: string) {
    markers.editEvent(id);
}
window.editEvent = editEvent

// utility for creating granule element on state map
// short version used when you don't need all the complexity, simply the rank containers (i.e. for city)
function createGranuleElement(short: boolean = false): Function {
    return (d) => {
        // setting id and class after appending causes expensive reflows, so we'll do it in 1 step
        let element = document.createElementNS(NS, "path");
        element.id = granularity[granule].stateId(d) + `_${granule}_state`;
        element.setAttribute('class', 'zip');
        if (short) {
            // we don't care about the map, only placeholders for ranks, generate cheapest possible path to save time
            element.setAttribute('d', 'M 0 0 l 1 1 l 0 1 z')
        } else {
            element.setAttribute('d', svgPath(d));
        }
        return element;
    }
}

// call this every time you change granularity
// short version used when you don't need all the complexity, simply the rank containers (i.e. for city)
function _updateStateGranules(short: boolean, geoJson: { features: [] }) {
    stateGranules
        .selectAll("path").remove()
    if (short) {
        stateGranules.selectAll("path")
            .data(geoJson.features.filter((d) => {
                if (granularity[granule].stateFilter && !granularity[granule].stateFilter(d)) {
                    return false
                }
                return true
            }))
            .enter().append(createGranuleElement(short))
            .exit().remove()
    } else {
        stateGranules.selectAll("path")
            .data(geoJson.features.filter((d) => {
                if (granularity[granule].stateFilter && !granularity[granule].stateFilter(d)) {
                    return false
                }
                return true
            }))
            .enter().append(createGranuleElement(short))
            .on("mouseover", (d) => {
                const id = granularity[granule].stateId(d)
                const name = granularity[granule].name(d)
                showTooltip(d, { title: name, loading: true })
                fetchAndShowTooltip(d)
                //utils.debounce(() => requestInfo(selectedYear, selectedStats, d.properties.ZCTA5CE10).then(data => showTooltip(d, tools.curateData(data))))
            })
            .on("mouseout", hideTooltip )
            .on("click", (d) => {
                cityMap.loadRegion(d.geometry.coordinates);
                cityMap.loadZipcodeMap(d);
                stateGranules.attr('display', 'none');
            }).exit().remove()
    }
}

// rerender zoomed-in state using higher quality GeoJSON file
function improveStateResolution(rankData: ZipsByRank, state: TopoJsonForZips, silentLoad: boolean) {

    // we send complete geography to zoom:enhance but render the simplified one at this level for better performance
    let presimplified = topojson.presimplify(state);
    let simplified = topojson.simplify(presimplified, 0.001/(scale**2) / globalMapScale); // fudge factor based on testing
    let simplifiedGeo = topojson.feature(simplified, state.objects[granularity[granule].plural]);

    // TODO: consider skipping all logic with silentLoad except zoom:enhance (passing it rankData for colors) for performance
    stateGranules
        .attr("class", zoomedInState)
        .attr("display", silentLoad ? "none" : "inline")
        .attr("transform", zipMap.attr("transform"))
        .style("stroke-width", zipMap.style("stroke-width"))
        .style('visibility', true)
    
    // draw SVG of state granules and attach mouse events to elements
    _updateStateGranules(silentLoad, simplifiedGeo)
    
    zipMap.node().style.display = "none";
    if (rankData) {
        applyRegionRanks(rankData);
    }
    pubSub.publish('zoom:enhance', {
        geo: topojson.feature(state, state.objects[granularity[granule].plural]),
        topo: state
    });
    if (!silentLoad) {
        pubSub.publish('notify:stopLoad');
        citySvgStateMap.attr("display", "")
    }
}

export function zoomOutState() {
    _cachedDelta = null;
    zoomedInCity = null;
    zoomedInState = null;
    zoomedInRegion = null
    cachedStates = {}
    history.pushState({
        region: null,
        year: controls.selectedYear,
        stats: controls.getSelectedStats(),
        granule,
    }, 'USA map', updateUrlOnZoomOrRanks());
    stateGranules.attr('display', 'none');
    if (needToDrawUs) { // we started with state view, so US was never drawn
        applyUsRanks()
        needToDrawUs = false
    }
    console.log('zoomout', needToRecomputeUs)
    if (needToRecomputeUs) {
        pubSub.publish('notify:startLoad', 'Recalculating Country...');
        requestRanks(selectedYear, selectedStats, void 0, granule).then(applyUsRanks).catch(error => {
            pubSub.publish('notify:stopLoad');
            pubSub.publish('notify:error', error);
        });
    }
    if (citiesVisible) {
        citySvgMap.attr("display", "")
        citySvgStateMap.attr("display", "none")
    }

    // force-rerender the markers on global map
    markers.loadMarkersD3(projection);
    markers.toggle();

    zipMap.node().style.display = "";
    zipMap.transition()
        .duration(750)
        .style("stroke-width", "0.5px")
        .attr("transform", "")
        .end()
        .then(() => {
            // unhide state map
            document.getElementById('states').style.display = "";
        });
    scale = 1;
    translate = [0, 0];
    hideTooltip();
    canvas.style.display = "";

    pubSub.publish('zoom:reset');
}
window.zoomOutState = zoomOutState

// remap back button to zoom back out to US level
window.addEventListener("popstate", (event) => {
    if (event.state.us) {
        zoomOutState();
    }
});

// map URL params to pre-loaded stats
let selectedYear: number = currentYear
let selectedStats: {} = {}
let queryParams = new URLSearchParams(window.location.search)
for (const [k, v] of queryParams) {
    if (k === 'year') {
        selectedYear = parseInt(v)
    } else if (k !== 'focus' && k !== 'granularity') {
        selectedStats[k] = v
    }
}

const categoryLegend = ((document.getElementById('category-legend'): any): HTMLElement)
const categoryLegendExpand = ((document.getElementById('category-legend-expand'): any): HTMLElement);
const categoryLegendClose = ((document.getElementById('category-legend-close'): any): HTMLElement);
const categoryLegendStats = ((document.getElementById('category-legend-stats'): any): HTMLElement)
function renderCategoryLegend(legend: [color, category, number][]) {
    categoryLegendStats.innerHTML = ''
    legend.forEach(([color, category, n], index) => {
        const prettyName = capitalize(category)
        const node = document.createElement("DIV");
        const colorBlock = document.createElement("DIV"); 
        const categoryName = document.createElement("SPAN");
        categoryName.classList.add('legend-name-box')
        categoryName.textContent = prettyName;
        colorBlock.classList.add('legend-color-box');
        colorBlock.style.background = color;
        node.id = `legend-rank-${index}`;
        node.classList.add('category-legend-box');
        node.appendChild(colorBlock);
        node.appendChild(categoryName);
        categoryLegendStats.appendChild(node);
        node.addEventListener('mouseover', () => {
            let message = `${n.toLocaleString()} zip codes`
            if (category !== 'no_data') {
                const group = Object.keys(selectedStats)[0]
                const stat = controls.getStat(`${group.slice(0, group.lastIndexOf(':'))}:${category}`)
                message = `${stat.info} (${message}).`
            }
            pubsub.publish('tooltip:show', {
                element: node,
                message
            })
        })
    })
    categoryLegend.style.display = 'block'
}
categoryLegendExpand.addEventListener('click', (e: MouseEvent) => {
    categoryLegendStats.style.display = ''
    categoryLegendClose.style.display = ''
    categoryLegendExpand.style.display = ''
})
categoryLegendClose.addEventListener('click', (e: MouseEvent) => {
    categoryLegendStats.style.display = 'none'
    categoryLegendClose.style.display = 'none'
    categoryLegendExpand.style.display = 'block'
})

// FUNCTIONS FOR COMPUTING AND APPLYING PRE-COMPUTED RANKS
// regenerate US map in graphics buffer for faster redraw
function generateBuffer() {
    let ctx = bufferCtx
    // ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = '#999';
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    Object.values(granularity[granule].usMapPaths).forEach(d => {
        bufferCanvasPath(d);
    })
    ctx.fill();
    ctx.stroke();
}
// redraws ranks for US
function applyUsRanks(rankedData: ?ZipsByRank) {
    let start = Date.now();
    ctx.drawImage(bufferCanvas, 0, 0)
    if (rankedData) {
        console.log(granularity[granule])
        Object.keys(rankedData).forEach((rankColor, rankIndex) => {
            ctx.lineWidth = 0.5;
            ctx.fillStyle = rankColor;
            rankedData[rankColor].zips.forEach(z => {
                let id = granularity[granule].normalizeId ? granularity[granule].normalizeId(z) : z
                var d = granularity[granule].usMapPaths[id]
                if (d) {
                    ctx.beginPath();
                    canvasPath(d);
                    ctx.fill();
                } else {
                    console.log(`Bad ${granule} granule: ${id}`)
                }
            });
        });
        console.log('applyUsRanks', needToRecomputeUs)
        needToRecomputeUs = false
        needToDrawUs = false
    }

    let keys = Object.keys(rankedData || {})
    if (keys.length) {
        const versus = rankedData[keys[0]].versus ? ['no_data', ...rankedData[keys[0]].versus] : null
        console.log("VV", versus)
        if (versus) {
            // versus
            renderCategoryLegend(versus.map((c, i) => {
                if (c === 'no_data') {
                    return ['#ffffff', c, 0]
                }
                const color = i === 1 ? categoricalGradient[0] : categoricalGradient[19]
                const sign = i === 1 ? 1 : -1
                const total = Array.from([0,1,2,3,4,5,6,7,8,9]).map(v => categoricalGradient[9 - v * sign]).reduce((a, v) => a + rankedData[v].zips.length, 0)
                return [color, c, total]
            }))
        } else if (rankedData[keys[0]].name) {
            // categories
            renderCategoryLegend(keys.map(c => [c, rankedData[c].name, rankedData[c].zips.length]))
        } else {
            categoryLegend.style.display = ''
        }
    }

    // @flow-ignore: temporary benchmark logic
    document.getElementById("render-time").innerHTML = `${Date.now() - start} (c)`;
    pubSub.publish('ranks:appliedCountry');
    pubSub.publish('notify:stopLoad');
}

// redraws ranks for state
function applyRegionRanks(rankedData: ZipsByRank) {
    console.log("GRANULES", rankedData)
    let start = Date.now();
    Array.prototype.forEach.call(document.getElementsByClassName('zip'), e => e.style.fill = '#fff')

    if (rankedData) {
        Object.keys(rankedData).forEach((rankColor, rankIndex) => {
            rankedData[rankColor].zips.forEach(z => {
                let id = granularity[granule].normalizeId ? granularity[granule].normalizeId(z) : z
                let select = ((document.getElementById(`${id}_${granule}_state`): any): HTMLElement);
                if (select) {
                    // @flow-ignore: SVG elements allow this property, flow doesn't know this
                    select.style.fill = rankColor;
                } else {
                    console.log(`Bad ${granule} granule: ${z}`)
                }
            })
        });
        let keys = Object.keys(rankedData)
        if (rankedData[keys[0]].name) {
            // categories detected
            renderCategoryLegend(keys.map(c => [c, rankedData[c].name, rankedData[c].zips.length]))
        } else {
            categoryLegend.style.display = ''
        }
    }

    document.getElementById("render-time").innerHTML = `${Date.now() - start} (c)`;
    pubSub.publish('ranks:appliedRegion', zoomedInCity ? zoomedInCity : zoomedInState);
    pubSub.publish('notify:stopLoad');
}

// fetches ranks for state or US and redraws them after response
function computeRanks(year: number, stats: { [string]: number | number[] }, local?: ZipsByRank) {
    timelineYear.innerText = year
    timelineStats.innerText = controls.humanFriendlyStats()

    selectedYear = year
    selectedStats = stats
    if (!local) {
        pubSub.publish('notify:startLoad', 'Analyzing...');
    }
    history.pushState({
        region: zoomedInRegion,
        year: year,
        stats: stats,
        granule,
    }, `heatmap update`, updateUrlOnZoomOrRanks());
    if (timelineObject.style.display && !local) {
        // if timeline is open, compute multiple years (triggered from "Compute" button)
        renderTimeline(year)
    } else {
        if (zoomedInRegion) {
            needToRecomputeUs = true
            if (local) {
                applyRegionRanks(local)
            } else {
                requestRanks(year, stats, zoomedInRegion, granule).then(ranks => {
                    if (!zoomedInCity) {
                        const region = `${zoomedInState}_${granule}`
                        if (cachedStates[region]) { // if we already have a cached computation of this granularity for this region
                            cachedStates[region].ranks = ranks // update ranks in our cache
                        }
                    }
                    return ranks
                }).then(applyRegionRanks)
            }
        } else {
            if (local) {
                applyUsRanks(local)
            } else {
                requestRanks(year, stats, void 0, granule).then(applyUsRanks)
            }
        }
    }
}
// similar to computeRanks but computes year change (delta)
// assume always local for now, unlike ranks - because we prefetch these during mouseup
function computeRanksDelta(startYear: number, delta: number, stats: { [string]: number }, local: ZipsByRank) {
    console.log("compuite ranks delta")
    timelineYear.innerText = `${startYear}-'${(startYear + delta) % 100}`
    selectedYear = startYear
    selectedDelta = delta
    selectedStats = stats
    history.pushState({
        region: zoomedInCity ? _getCity(cachedRegion) : zoomedInState ? { state: zoomedInState } : null,
        year: startYear,
        delta: delta,
        stats: stats,
        granule,
    }, `heatmap update`, updateUrlOnZoomOrRanks());
    if (zoomedInState) {
        needToRecomputeUs = true
        applyRegionRanks(local)
    } else {
        applyUsRanks(local)
    }
}

// timeline
let blackSwanEvents = {
    '2000.9': 'Dot-Com Bust',
    '2007.3': 'Financial Crisis',
    '2020.25': 'COVID-19'
}

let timelineContainer = ((document.getElementById('timeline-container'): any): HTMLElement);
let timelineObject = ((document.getElementById('timeline'): any): HTMLElement);
function hideTimeline(e: MouseEvent) {
    e.stopPropagation()
    timelineContainer.style.width = ''
    timelineObject.style.display = ''
    timelineYearLabel.style.left = ''
    timelineYear.style.left = ''
    timelineContainer.style.border = ''
    timelineContainer.style.display = ''
    timelineStats.style.display = ''
}
timelineYear.addEventListener('click', () => {
    if (timelineObject.style.display) {
        // hide
        hideTimeline()
    } else {
        // show
        timelineContainer.style.border = '1px solid rgb(235, 235, 235)'
        timelineContainer.style.width = `${mapDimensions.width}px`
        timelineContainer.style.display = 'block'
        renderTimeline(selectedYear)
        timelineObject.style.display = 'block'
        timelineStats.style.display = 'none'
    }
})
timelineClose.addEventListener('mousedown', hideTimeline) // mousedown prevents other timeline mousedown logic from triggering
let timelineScale = ((document.getElementById('timeline-time'): any): HTMLElement);

let timelineStart = 1995
let timelineEnd = 2025
let _cachedRankYears = {}

let timelineEvents = ((document.getElementById('timeline-history-events'): any): HTMLElement);
let timelineMyEvents = ((document.getElementById('timeline-my-events'): any): HTMLElement);
let timelineSlider = ((document.getElementById('timeline-slider'): any): HTMLElement);
let dragBar = ((document.getElementById('timeline-slider-dragbar'): any): HTMLElement);
let sliderPosition = 0
let sliderDelta = 0

let addTimelineElement = (year: number, text?: string, timeline: HTMLElement, cls?: string) => {
    let position = 100 * (year - timelineStart) / (timelineEnd - timelineStart);
    if (position > 0 && position < 100) {
        let event = document.createElement('DIV')
        event.classList.add('timeline-item')
        cls && (event.classList.add(cls))
        text && (event.innerText = text) 
        event.style.left = `${position}%`
        timeline.appendChild(event)
    }
}
function renderTimeline(centerYear?: number) {
    if (centerYear) {
        centerYear = parseInt(centerYear)
        timelineStart = centerYear - 20
        timelineEnd = centerYear + 9
    }
    let region = zoomedInCity ? _getCity(cachedRegion) : zoomedInState ? { state: zoomedInState } : undefined

    sliderPosition = 100 * ((centerYear || selectedYear) - timelineStart) / (timelineEnd - timelineStart)
    timelineSlider.style.left = `${sliderPosition + sliderDelta}%`
    timelineYearLabel.style.left = `${sliderPosition}%`
    timelineYear.style.left = `${sliderPosition}%`

    if (selectedDelta) {
        requestDeltaRanksMultiYear(timelineStart - selectedDelta, timelineEnd, selectedDelta, selectedStats, region).then(multiYearRanks => {
            _cachedRankYears = multiYearRanks
            if (centerYear) {
                computeRanksDelta(centerYear, selectedDelta, controls.getSelectedStats(), _cachedRankYears[centerYear])
                giveWarnings()
            }
        }).catch(e => {
            // probably not implemented error for categories
            pubSub.publish('notify:error', e.message)
        })
    } else {
        requestRanksMultiYear(timelineStart, timelineEnd, selectedStats, region, granule).then(multiYearRanks => {
            _cachedRankYears = multiYearRanks
            console.log('multiYearRanks', multiYearRanks)
            if (centerYear) {
                computeRanks(centerYear, controls.getSelectedStats(), _cachedRankYears[centerYear])
                giveWarnings()
            }
        })
    }

    // remove old events
    Array.from(document.getElementsByClassName('timeline-item')).forEach(e => e.remove())

    // generate timescale
    for (let y = timelineStart; y < timelineEnd; y++) {
        let halfDecade = !(y % 5)
        addTimelineElement(y, halfDecade && y, timelineScale, halfDecade && 'half-decade')
    }

    // populate black swan events
    Object.keys(blackSwanEvents).forEach(year => {
        addTimelineElement(parseFloat(year), blackSwanEvents[year], timelineEvents)
    })

    // place slider
    //timelineSlider.style.left = 100 * (selectedYear - timelineStart) / (timelineEnd - timelineStart) + '%'
}

let mapContainer = ((document.getElementById('map-container'): any): HTMLElement);
let timelineBwd = ((document.getElementById('timeline-bwd'): any): HTMLElement);
let timelineFwd = ((document.getElementById('timeline-fwd'): any): HTMLElement);
// these are mousedowns (rather than clicks) because we listen to mousedown event later, which fires before click and needs to be cancelled here
timelineBwd.addEventListener('mousedown', (e: MouseEvent) => {
    e.stopPropagation()
    moveTimeline(-10)
})
timelineFwd.addEventListener('mousedown', (e: MouseEvent) => {
    e.stopPropagation()
    moveTimeline(10)
})
function moveTimeline(years: number) {
    timelineStart += years
    timelineEnd += years
    renderTimeline()
}

// update timeline slider position (current year)
let dragging = false;
let _updateSlider = (offset: number) => {
    let year =  parseInt(((offset / timelineContainer.offsetWidth) - sliderDelta / 100) * (timelineEnd - timelineStart) + timelineStart)
    sliderPosition = 100 * (offset / timelineContainer.offsetWidth) - sliderDelta
    timelineSlider.style.left = `${sliderPosition + sliderDelta}%`
    timelineYearLabel.style.left = `${sliderPosition}%` 
    timelineYear.style.left = `${sliderPosition}%`
    if (parseInt(selectedYear) != year) {
        selectedYear = year
        //timelineSlider.innerText = `Current Year (${year})`
        if (!selectedDelta) {
            // single year
            computeRanks(year, controls.getSelectedStats(), _cachedRankYears[year]);
        } else {
            // delta
            computeRanksDelta(year, selectedDelta, controls.getSelectedStats(), _cachedRankYears[year]);
        }
        giveWarnings()
    }
}
let updateSlider = (e: MouseEvent) => {
    if (dragging) {
        e.preventDefault()
        e.stopPropagation()

        console.log(e.offsetX, e.clientX, e.pageX, e.x, timelineObject.offsetWidth, e)
        let rect = timelineContainer.getBoundingClientRect()
        _updateSlider(e.clientX - rect.left)
        //_updateSlider(e.offsetX)
    }
}
dragBar.addEventListener('mousedown', (e: MouseEvent) => {
    timelineSlider.style.cursor = 'grabbing'
    e.stopPropagation()
    dragging = true;
    timelineSlider.style.pointerEvents = 'none'
    let rect = timelineContainer.getBoundingClientRect()
    _updateSlider(e.clientX - rect.left)
    //_updateSlider(timelineSlider.offsetLeft - timelineObject.offsetLeft + e.offsetX)
})
timelineObject.addEventListener('mousedown', (e: MouseEvent) => {
    timelineSlider.style.cursor = 'grabbing'
    if (e.target.parentElement !== deltaLeft && e.target.parentElement !== deltaRight) {
        dragging = true;
        timelineSlider.style.pointerEvents = 'none'
        updateSlider(e)
    }
})
timelineObject.addEventListener('mousemove', updateSlider)
mapContainer.addEventListener('mouseleave', (e: MouseEvent) => {
    timelineSlider.style.cursor = ''
    dragging = false;
    timelineSlider.style.pointerEvents = ''
})
timelineObject.addEventListener('mouseup', (e: MouseEvent) => {
    timelineSlider.style.cursor = ''
    dragging = false;
    draggingDeltaLeft = false;
    draggingDeltaRight = false;
    timelineSlider.style.pointerEvents = ''
    if (selectedDelta) {
        _onMouseUpDelta()
    }
})

// delta operations
let selectedDelta = 0
let deltaLeft = ((document.getElementById('timeline-slider-delta-left'): any): HTMLElement);
let deltaRight = ((document.getElementById('timeline-slider-delta-right'): any): HTMLElement);
let draggingDeltaLeft = false;
let draggingDeltaRight = false;
function _updateDelta(offset: number) {
    let finalWidth
    if (draggingDeltaLeft) {
        let right = timelineContainer.offsetWidth * (sliderPosition + 2 * sliderDelta) / 100
        finalWidth = right - offset
        sliderPosition = 100 * offset / timelineContainer.offsetWidth
        sliderDelta = (100 * finalWidth / 2) / timelineContainer.offsetWidth
    } else {
        let left = timelineContainer.offsetWidth * sliderPosition / 100
        finalWidth = offset - left
        sliderDelta = (100 * finalWidth / 2) / timelineContainer.offsetWidth
        if (sliderDelta < 0) {
            sliderPosition = 100 * offset / timelineContainer.offsetWidth
        }
    }
    sliderDelta = Math.max(0, sliderDelta)
    dragBar.style.width = finalWidth + 'px'
    timelineSlider.style.left = `${sliderPosition + sliderDelta}%`

    selectedYear = parseInt((sliderPosition / 100) * (timelineEnd - timelineStart) + timelineStart)
    selectedDelta = parseInt(2 * sliderDelta / 100 * (timelineEnd - timelineStart)) // temporary delta, we'll recompute a more accurate version

    // update year
    if (selectedDelta) {
        // note: our delta will sometimes result in endYear being 1 year off due to trying to map continous timeline object to discrete years
        // to minimize confusion, we base our delta on year observed here, even though it may not always be accurate with slider moving
        let endYear = parseInt(((sliderPosition + 2 * sliderDelta) / 100) * (timelineEnd - timelineStart) + timelineStart)

        timelineYearLabel.innerText = 'Change Between'
        timelineYear.innerText = `${selectedYear}-'${(endYear) % 100}`
    } else {
        timelineYearLabel.innerText = 'Year'
        timelineYear.innerText = `${selectedYear}`
    }
    timelineYearLabel.style.left = `${sliderPosition}%` 
    timelineYear.style.left = `${sliderPosition}%`
}
function updateDelta(e: MouseEvent) {
    if (draggingDeltaLeft || draggingDeltaRight) {
        e.preventDefault()
        e.stopPropagation()
        let rect = timelineContainer.getBoundingClientRect()
        _updateDelta(e.clientX - rect.left)
    }
}

deltaLeft.addEventListener('mousedown', (e: MouseEvent) => {
    timelineSlider.style.cursor = 'grabbing'
    e.preventDefault()
    e.stopPropagation()
    timelineSlider.style.pointerEvents = 'none'
    draggingDeltaLeft = true;
})
deltaRight.addEventListener('mousedown', (e: MouseEvent) => {
    timelineSlider.style.cursor = 'grabbing'
    e.preventDefault()
    e.stopPropagation()
    timelineSlider.style.pointerEvents = 'none'
    draggingDeltaRight = true;
})

let zoomedInRegion = null
// prefetches deltas for all years visible in the timeline
let _cachedDelta = null
let _onMouseUpDelta = () => {
    timelineSlider.style.cursor = ''
    timelineSlider.style.pointerEvents = ''

    if (_cachedDelta === selectedDelta) {
        computeRanksDelta(selectedYear, selectedDelta, controls.getSelectedStats(), _cachedRankYears[selectedYear]);
        giveWarnings()
        return
    }

    let region = zoomedInCity ? _getCity(cachedRegion) : zoomedInState ? { state: zoomedInState } : undefined
    requestDeltaRanksMultiYear(timelineStart - selectedDelta, timelineEnd, selectedDelta, selectedStats, region, granule).then(multiYearRanks => {
        _cachedRankYears = multiYearRanks
        _cachedDelta = selectedDelta
        computeRanksDelta(selectedYear, selectedDelta, controls.getSelectedStats(), _cachedRankYears[selectedYear]);
        giveWarnings()
    }).catch(e => {
        console.log('detla err')
        // probably not implemented error for categories
        pubSub.publish('notify:error', e.message)
    })
}
deltaLeft.addEventListener('mouseup', () => {
    _onMouseUpDelta()
    draggingDeltaLeft = false;
})
deltaRight.addEventListener('mouseup', () => {
    _onMouseUpDelta()
    draggingDeltaRight = false;
})
timelineObject.addEventListener('mousemove', updateDelta)
mapContainer.addEventListener('mouseleave', (e: MouseEvent) => {
    if (draggingDeltaLeft || draggingDeltaRight) {
        _onMouseUpDelta()
        draggingDeltaLeft = false;
        draggingDeltaRight = false;
    }
})
